import React, {ReactElement} from "react"
import {AllowedIncomingAchColumns, IncomingAchColumns} from "./IncomingAch.types"
import numeral from "numeral"
import moment from "moment"
import {kebabCase, startCase} from "lodash"
import classNames from "classnames"
import {useNavigate} from "react-router-dom"
import {IncomingAch} from "../../resources/incomingAch"
import {Resource} from "../../resources/common"
import {Bank} from "../../resources/bank"
import {DataTableCell, DataTableRow} from "../../components/DataTable/DataTable"
import {OrgName} from "../../components/OrgName/OrgName"
import IdLink from "../../components/IdLink/IdLink"

interface IncomingAchsTableRowProps {
	incomingAch: IncomingAch
	include?: Resource[]
	showOrgColumn?: boolean
	includedColumns: AllowedIncomingAchColumns[]
	onClick?: (e: React.MouseEvent<HTMLTableRowElement>) => void
}

export function IncomingAchsTableRow({incomingAch, include, includedColumns, onClick}: IncomingAchsTableRowProps) {
	const navigate = useNavigate()
	const id = incomingAch.id
	const accountId = incomingAch.relationships.account?.data.id
	const customerId = incomingAch.relationships.customer?.data.id
	const bankId = incomingAch.relationships.bank?.data.id
	const org = incomingAch.relationships.org?.data.id
	const amount = incomingAch.attributes.item.amount
	const status = incomingAch.attributes.status
	const createdAt = incomingAch.attributes.createdAt

	const customer =
		customerId && include
			? include.find(
					(r) =>
						(r.type == "individualCustomer" || r.type == "businessCustomer" || r.type == "businessFBOCustomer") &&
						r.id == customerId.toString()
			  )
			: undefined
	let customerName: string | undefined = undefined

	if (customer) {
		if (customer.type == "individualCustomer") {
			customerName = `${customer.attributes.fullName.first} ${customer.attributes.fullName.last}`
		} else if (customer.type == "businessCustomer" || customer.type == "businessFBOCustomer") {
			customerName = customer.attributes.name
		}
	}

	const bank =
		bankId != null && include ? include.find((r): r is Bank => r.type === "bank" && r.id === String(bankId)) : undefined
	const bankName = bank?.attributes?.name

	const contentColumns: Partial<Record<AllowedIncomingAchColumns, ReactElement>> = {}

	if (includedColumns.includes(IncomingAchColumns.id)) {
		contentColumns["Id"] = (
			<DataTableCell className={"data-table-id-cell"}>
				<IdLink className="data-table-id" href={`/incoming-achs/${id}`}>
					{id}
				</IdLink>
			</DataTableCell>
		)
	}

	if (includedColumns.includes(IncomingAchColumns.customer)) {
		contentColumns["Customer"] = (
			<DataTableCell>
				<a
					className="link"
					onClick={(e) => {
						e.preventDefault()
						e.stopPropagation()
						if (customer) {
							navigate(`/${customer.type == "individualCustomer" ? "individual" : "business"}/${customerId}`)
						}
					}}
				>
					{customerName}
				</a>
			</DataTableCell>
		)
	}

	if (includedColumns.includes(IncomingAchColumns.account)) {
		contentColumns["Account"] = (
			<DataTableCell>
				<a
					className="link"
					onClick={(e) => {
						e.preventDefault()
						e.stopPropagation()
						if (accountId) {
							navigate(`/accounts/${accountId}`)
						}
					}}
				>
					{accountId}
				</a>
			</DataTableCell>
		)
	}

	if (includedColumns.includes(IncomingAchColumns.bank)) {
		contentColumns["Bank"] = <DataTableCell>{bankName ?? "N/A"}</DataTableCell>
	}

	if (includedColumns.includes(IncomingAchColumns.org)) {
		contentColumns["Org"] = org ? (
			<OrgName orgId={org.toString()} included={include} element={DataTableCell} />
		) : (
			<DataTableCell>N/A</DataTableCell>
		)
	}

	if (includedColumns.includes(IncomingAchColumns.amount)) {
		contentColumns["Amount"] = (
			<DataTableCell>
				<span className="payments-amount">{numeral(amount / 100).format("$0,0.00")}</span>
			</DataTableCell>
		)
	}

	if (includedColumns.includes(IncomingAchColumns.status)) {
		contentColumns["Status"] = (
			<DataTableCell>
				<div
					data-tip="React-tooltip"
					data-for={`status-reason-tooltip-${id}`}
					className={classNames("incomingach-status", kebabCase(status))}
				>
					{startCase(status)}
				</div>
			</DataTableCell>
		)
	}

	if (includedColumns.includes(IncomingAchColumns.createdAt)) {
		contentColumns["Created At"] = <DataTableCell>{moment(createdAt).format("L LT")}</DataTableCell>
	}

	const content: Array<ReactElement | null> = []

	includedColumns.forEach((col, i) => {
		if (col in contentColumns && contentColumns[col]) {
			const column = {...contentColumns[col]} as ReactElement
			column.key = i.toString()
			content.push(column)
		}
	})

	return <DataTableRow onClick={(e) => onClick && onClick(e)}>{content}</DataTableRow>
}
